import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { formatError } from "helpers/formatError";
import { ENTITY_ORIGINS } from "routes/revenue_opportunity/paths";
import {
  originFromScope,
  useRevenueOpportunityNavigation,
} from "routes/revenue_opportunity/useRevenueOpportunityNavigation";
import CampaignService from "services/Campaign";

// Campaign.status 1 is Active (constants/Campaign.js).
const ACTIVE_CAMPAIGN_STATUS = 1;

const fetchActiveCampaigns = async entityId => {
  const campaigns = await CampaignService.getCampaignsForFilters({
    entity_id: entityId,
  });
  return campaigns.filter(
    campaign => campaign.status === ACTIVE_CAMPAIGN_STATUS
  );
};

/**
 * Starts the create flow from a table scoped by a parent record. From an
 * Entity, Contact, or Case page the campaign comes from the entity's active
 * campaigns: none leaves it empty, one is preselected, and several are put to
 * the user, who may pick one, pick None, or cancel the create.
 */
export const useStartCreateRevenueOpportunity = scope => {
  const queryClient = useQueryClient();
  const { openCreate } = useRevenueOpportunityNavigation();
  const [isStarting, setIsStarting] = useState(false);
  const [campaignChoices, setCampaignChoices] = useState(null);

  const origin = originFromScope(scope);
  const preset = { ...scope, origin };
  const resolvesCampaign =
    ENTITY_ORIGINS.includes(origin) && !!scope.entityId && !scope.campaignId;

  const openWithCampaign = campaignId => {
    setCampaignChoices(null);
    openCreate({ ...preset, campaignId });
  };

  const start = async () => {
    if (!resolvesCampaign) {
      openCreate(preset);
      return;
    }
    setIsStarting(true);
    try {
      const campaigns = await queryClient.fetchQuery({
        queryKey: ["entity-active-campaigns", Number(scope.entityId)],
        queryFn: () => fetchActiveCampaigns(scope.entityId),
      });
      if (campaigns.length > 1) {
        setCampaignChoices(campaigns);
      } else {
        openWithCampaign(campaigns[0]?.id ?? null);
      }
    } catch (error) {
      toast.error(formatError(error, "Could not load the entity's campaigns"));
    } finally {
      setIsStarting(false);
    }
  };

  return {
    start,
    isStarting,
    dialogProps: {
      open: campaignChoices != null,
      campaigns: campaignChoices ?? [],
      onSelect: openWithCampaign,
      onCancel: () => setCampaignChoices(null),
    },
  };
};
